import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './CreatePatient.css';
import IraqiPhoneValidator from '../../utils/IraqiPhoneValidator';

export default function CreatePatient() {
    const [formData, setFormData] = useState({
        name: '',
        phone_number: '',
        birthdate: '',
        description: ''
    });
    const [appointmentNote, setAppointmentNote] = useState('First appointment');
    const [appointmentDate, setAppointmentDate] = useState(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dupLoading, setDupLoading] = useState(false);
    const [dupError, setDupError] = useState('');
    const [duplicatePatients, setDuplicatePatients] = useState([]);
    const API_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    axios.defaults.withCredentials = true;


    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'phone_number') {
            // Format the phone number for Iraqi format using validator
            const formattedPhone = IraqiPhoneValidator.formatPhoneNumber(value);
            setFormData(prev => ({
                ...prev,
                [name]: formattedPhone
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Check for existing patients when name OR phone is filled (debounced)
    useEffect(() => {
        setDupError('');
        setDuplicatePatients([]);
        if (!formData.name && !formData.phone_number) return;

        const controller = new AbortController();
        const t = setTimeout(async () => {
            try {
                setDupLoading(true);
                const params = new URLSearchParams();
                if (formData.name) params.append('name', formData.name);
                if (formData.phone_number) params.append('phone_number', formData.phone_number);
                
                const res = await axios.get(`${API_URL}patients/find-by-name-phone?${params.toString()}` , {
                    headers: { 'Accept': 'application/json' },
                    signal: controller.signal,
                });
                if (Array.isArray(res.data) && res.data.length > 0) {
                    setDuplicatePatients(res.data);
                } else {
                    setDuplicatePatients([]);
                }
            } catch (e) {
                if (e.name !== 'CanceledError') {
                    setDupError('Could not check existing patients');
                }
            } finally {
                setDupLoading(false);
            }
        }, 500);

        return () => {
            clearTimeout(t);
            controller.abort();
        };
    }, [formData.name, formData.phone_number, API_URL]);

    // Handle birthdate changes for year-month-day selector (avoid UTC shift)
    const handleBirthdateChange = (type, value) => {
        if (!value) {
            if (type === 'year') {
                setFormData(prev => ({ ...prev, birthdate: '' }));
            }
            return;
        }

        // Parse existing parts safely from YYYY-MM-DD without using Date()
        let y, m, d;
        if (formData.birthdate) {
            const parts = formData.birthdate.split('-');
            y = parseInt(parts[0]);
            m = parseInt(parts[1]); // 1-12
            d = parseInt(parts[2]);
        } else {
            const today = new Date();
            y = today.getFullYear();
            m = today.getMonth() + 1; // 1-12
            d = today.getDate();
        }

        switch (type) {
            case 'year':
                y = parseInt(value);
                break;
            case 'month':
                m = parseInt(value); // keep 1-12
                break;
            case 'day':
                d = parseInt(value);
                break;
            default:
                break;
        }

        // Validate day doesn't exceed days in selected month/year (use local math)
        const daysInMonth = new Date(y, m, 0).getDate(); // using 1-based m
        if (d > daysInMonth) {
            d = daysInMonth;
        }

        const formattedDate = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        setFormData(prev => ({ ...prev, birthdate: formattedDate }));
    };

    // Get number of days in the selected month/year
    const getDaysInMonth = () => {
        if (!formData.birthdate) return 31;
        const [yearStr, monthStr] = formData.birthdate.split('-');
        const year = parseInt(yearStr);
        const month = parseInt(monthStr); // 1-12
        return new Date(year, month, 0).getDate();
    };

    // Calculate age from birthdate
    const calculateAge = (birthdate) => {
        if (!birthdate) return null;
        
        const today = new Date();
        const birth = new Date(birthdate);
        
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        
        return age;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        // No validation needed for appointment note - it's optional
        
        setLoading(true);

        try {
                        const submissionData = {
                ...formData,
                description: formData.description ? 
                    `${formData.description}` : 
                    `Patient created`
            };
            
            // First, create the patient
            const patientResponse = await axios.post(`${API_URL}patients`, submissionData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (patientResponse.status === 201) {
                // Create appointment note only if provided (optional)
                if (appointmentNote.trim()) {
                    const noteData = {
                        appointment_note: appointmentNote,
                        type: 'appointment',
                        date: appointmentDate || new Date().toISOString().split('T')[0]
                    };

                    await axios.post(
                        `${API_URL}patients/${patientResponse.data.id}/notes`,
                        noteData,
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept': 'application/json'
                            }
                        }
                    );
                }
                
                // Redirect to the newly created patient's detail page
                navigate(`/control/patient/${patientResponse.data.id}`);
            } else {
                setError('Failed to create patient');
            }
        } catch (err) {
            if (err.response && err.response.status === 401) {
                setError('Unauthorized. Please login again.');
            } else if (err.response && err.response.status === 403) {
                // Handle restricted account error
                const errorMessage = err.response.data?.message || err.response.data?.error || 'Access denied';
                setError(errorMessage);
            } else {
                setError('Error creating patient: ' + err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="create-patient-container">
            <div className="container mt-4">
                <div className="row justify-content-center">
                    <div className="col-md-10">
                        <div className="create-patient-card">
                            <div className="create-patient-header">
                                <h2>Create New Patient</h2>
                                <p className="create-patient-subtitle">Add a new patient to the clinic</p>
                            </div>
                            <div className="create-patient-body">
                                {error && (
                                    <div className="error-message">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        {error}
                                    </div>
                                )}
                                {/* Duplicate patient warning */}
                                {(duplicatePatients.length > 0 || dupError) && (
                                    <div className="duplicate-patients-alert">
                                        <div className="duplicate-patients-header">
                                            <i className="fas fa-user-check"></i>
                                            <strong>Possible Existing Patient{duplicatePatients.length > 1 ? 's' : ''}</strong>
                                        </div>
                                        {dupLoading && (
                                            <div className="duplicate-patients-loading">
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Checking for existing patients...
                                            </div>
                                        )}
                                        {dupError && (
                                            <div className="duplicate-patients-error">{dupError}</div>
                                        )}
                                        {duplicatePatients.length > 0 && (
                                            <div className="duplicate-patients-content">
                                                <div className="duplicate-patients-count">
                                                    Found {duplicatePatients.length} matching patient{duplicatePatients.length > 1 ? 's' : ''}:
                                                </div>
                                                <div className="duplicate-patients-list">
                                                    {duplicatePatients.map(p => (
                                                        <div 
                                                            key={p.id} 
                                                            className="duplicate-patient-item"
                                                            onClick={() => navigate(`/control/patient/${p.id}`)}
                                                        >
                                                            <div className="duplicate-patient-info">
                                                                <div className="duplicate-patient-name">
                                                                    <i className="fas fa-user-circle me-2"></i>
                                                                    {p.name}
                                                                </div>
                                                                <div className="duplicate-patient-phone">
                                                                    <i className="fas fa-phone me-2"></i>
                                                                    {p.phone_number || 'No phone'}
                                                                </div>
                                                            </div>
                                                            <div className="duplicate-patient-action">
                                                                <i className="fas fa-arrow-right"></i>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <form onSubmit={handleSubmit} dir="ltr">
                                    <div className="patient-form-section">
                                        <h4>
                                            <i className="fas fa-user"></i>
                                            Patient Information
                                        </h4>
                                        
                                        <div className="patient-form-group">
                                            <label htmlFor="name" className="patient-form-label">Full Name</label>
                                            <input
                                                type="text"
                                                className="patient-form-control"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Enter patient's full name"
                                                required
                                            />
                                        </div>
                                        
                                        <div className="patient-form-group">
                                            <label htmlFor="phone_number" className="patient-form-label">Phone Number</label>
                                            <input
                                                type="tel"
                                                className="patient-form-control"
                                                id="phone_number"
                                                name="phone_number"
                                                value={formData.phone_number}
                                                onChange={handleChange}
                                                placeholder={IraqiPhoneValidator.getPlaceholder()}
                                                required
                                                inputMode="numeric"
                                            />
                                            <div className="form-help-text">
                                                {IraqiPhoneValidator.getHelpText()}
                                            </div>
                                        </div>
                                        
                                        <div className="patient-form-group">
                                            <label htmlFor="birthdate" className="patient-form-label">
                                                Birth Date (Optional)
                                                {formData.birthdate && calculateAge(formData.birthdate) !== null && (
                                                    <span className="text-muted ms-2" style={{ fontSize: '0.9em', fontWeight: 'normal' }}>
                                                        (Age: {calculateAge(formData.birthdate)} years)
                                                    </span>
                                                )}
                                            </label>
                                            <div className="birthdate-selector">
                                                <div className="row g-2">
                                                    <div className="col-4">
                                                        <select
                                                            className="form-select patient-form-control"
                                                            value={formData.birthdate ? formData.birthdate.split('-')[0] : ''}
                                                            onChange={(e) => handleBirthdateChange('year', e.target.value)}
                                                        >
                                                            <option value="">Year</option>
                                                            {Array.from({ length: 100 }, (_, i) => {
                                                                const year = new Date().getFullYear() - i;
                                                                return (
                                                                    <option key={year} value={year}>
                                                                        {year}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                    <div className="col-4">
                                                        <select
                                                            className="form-select patient-form-control"
                                                            value={formData.birthdate ? parseInt(formData.birthdate.split('-')[1]) : ''}
                                                            onChange={(e) => handleBirthdateChange('month', e.target.value)}
                                                            disabled={!formData.birthdate || !formData.birthdate.split('-')[0]}
                                                        >
                                                            <option value="">Month</option>
                                                            {[
                                                                'January', 'February', 'March', 'April', 'May', 'June',
                                                                'July', 'August', 'September', 'October', 'November', 'December'
                                                            ].map((month, index) => (
                                                                <option key={index + 1} value={index + 1}>
                                                                    {month}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="col-4">
                                                        <select
                                                            className="form-select patient-form-control"
                                                            value={formData.birthdate ? parseInt(formData.birthdate.split('-')[2]) : ''}
                                                            onChange={(e) => handleBirthdateChange('day', e.target.value)}
                                                            disabled={!formData.birthdate || !formData.birthdate.split('-')[1]}
                                                        >
                                                            <option value="">Day</option>
                                                            {Array.from({ length: getDaysInMonth() }, (_, i) => (
                                                                <option key={i + 1} value={i + 1}>
                                                                    {i + 1}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="patient-form-group">
                                            <label htmlFor="description" className="patient-form-label">Demographic, past and social history (Optional)</label>
                                            <textarea
                                                className="patient-form-control patient-textarea"
                                                id="description"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                placeholder="Additional notes about the patient..."
                                                style={{
                                                    whiteSpace: 'pre-wrap',
                                                    minHeight: '200px',
                                                    maxHeight: '40vh',
                                                    resize: 'vertical',
                                                    overflowY: 'auto'
                                                }}
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="patient-form-section">
                                        <h4>
                                            <i className="fas fa-calendar-plus"></i>
                                            Appointment Note (Required)
                                        </h4>
                                        
                                        <div className="appointment-date-section">
                                            <div className="patient-form-group">
                                                <label htmlFor="appointmentDate" className="patient-form-label">Appointment Date</label>
                                                <input
                                                    type="date"
                                                    className="date-input"
                                                    id="appointmentDate"
                                                    value={appointmentDate}
                                                    onChange={(e) => setAppointmentDate(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="patient-form-group">
                                            <label htmlFor="appointmentNote" className="patient-form-label">Appointment Note</label>
                                            <input
                                                type="text"
                                                className="patient-form-control"
                                                id="appointmentNote"
                                                value={appointmentNote}
                                                onChange={(e) => setAppointmentNote(e.target.value)}
                                                placeholder="Brief appointment summary (one sentence)"
                                                maxLength={255}
                                            />
                                            <div className="form-help-text">This note will be saved as an appointment note for this patient.</div>
                                        </div>
                                    </div>
                                    
                                    <div className="form-buttons">
                                        <button 
                                            type="button" 
                                            className="cancel-btn"
                                            onClick={() => navigate(-1)}
                                        >
                                            <i className="fas fa-times"></i>
                                            Cancel
                                        </button>
                                        <button 
                                            type="submit" 
                                            className="create-btn"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <div className="spinner-border" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    Creating...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-plus"></i>
                                                    Create Patient
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    {error && (
                                        <div className="error-message mt-3">
                                            <i className="fas fa-exclamation-triangle"></i>
                                            {error}
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
